/*
  <variant-dropdown> — a disclosure that presents a fieldset of hidden
  radios as a trigger button and a scrollable menu. It owns open/close and
  the trigger's text only. Selection is the radios' own: clicking a label
  checks its radio, the `change` event bubbles to the theme's
  <variant-radios> (global.js), and price, add-to-cart, URL and media update
  exactly as they do for pills. Added 7 September 2026.

  A pointer selection closes the menu on the next tick, AFTER the radio's
  own change event has fired and bubbled; keyboard arrows change the checked
  radio while the menu stays open, so the shopper can keep moving.
*/
(function () {
  if (customElements.get('variant-dropdown')) return;

  class VariantDropdown extends HTMLElement {
    constructor() {
      super();
      this.onTriggerClick = this.onTriggerClick.bind(this);
      this.onTriggerKeydown = this.onTriggerKeydown.bind(this);
      this.onMenuClick = this.onMenuClick.bind(this);
      this.onMenuKeydown = this.onMenuKeydown.bind(this);
      this.onChange = this.onChange.bind(this);
      this.onDocumentClick = this.onDocumentClick.bind(this);
    }

    connectedCallback() {
      this.trigger = this.querySelector('.variant-dropdown__trigger');
      this.menu = this.querySelector('.variant-dropdown__menu');
      this.valueEl = this.querySelector('.variant-dropdown__value');
      if (!this.trigger || !this.menu) return;
      this.trigger.addEventListener('click', this.onTriggerClick);
      this.trigger.addEventListener('keydown', this.onTriggerKeydown);
      this.menu.addEventListener('click', this.onMenuClick);
      this.menu.addEventListener('keydown', this.onMenuKeydown);
      this.addEventListener('change', this.onChange);
      this.syncValue();
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocumentClick, true);
    }

    get isOpen() {
      return !this.menu.hidden;
    }

    open() {
      if (this.isOpen) return;
      document.querySelectorAll('variant-dropdown').forEach((other) => {
        if (other !== this) other.close();
      });
      this.menu.hidden = false;
      this.classList.add('open');
      this.trigger.setAttribute('aria-expanded', 'true');
      document.addEventListener('click', this.onDocumentClick, true);
      this.menu.scrollIntoView({ block: 'nearest' });
      const checked = this.menu.querySelector('input[type="radio"]:checked') || this.menu.querySelector('input[type="radio"]');
      if (checked) {
        checked.focus({ preventScroll: true });
        const row = checked.closest('.variant-dropdown__item');
        if (row && typeof row.scrollIntoView === 'function') row.scrollIntoView({ block: 'nearest' });
      }
    }

    close(returnFocus) {
      if (!this.isOpen) return;
      this.menu.hidden = true;
      this.classList.remove('open');
      this.trigger.setAttribute('aria-expanded', 'false');
      document.removeEventListener('click', this.onDocumentClick, true);
      if (returnFocus) this.trigger.focus();
    }

    syncValue() {
      const checked = this.querySelector('input[type="radio"]:checked');
      if (!checked || !this.valueEl) return;
      const label = this.querySelector('label[for="' + checked.id + '"] .variant-dropdown__text');
      this.valueEl.textContent = label ? label.textContent.trim() : checked.value;
    }

    onTriggerClick(event) {
      event.preventDefault();
      if (this.isOpen) {
        this.close();
      } else {
        this.open();
      }
    }

    onTriggerKeydown(event) {
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        this.open();
      } else if (event.key === 'Escape' && this.isOpen) {
        event.preventDefault();
        this.close(true);
      }
    }

    onMenuClick(event) {
      const row = event.target.closest ? event.target.closest('.variant-dropdown__item') : null;
      if (!row) return;
      // The label's click checks its radio and fires `change` synchronously
      // as part of this same click's default action; closing on the next
      // tick lets that finish (and lets a click on the current row close
      // too, which fires no change at all).
      setTimeout(() => {
        this.syncValue();
        this.close(true);
      }, 0);
    }

    onMenuKeydown(event) {
      if (event.key === 'Escape' || event.key === 'Enter') {
        event.preventDefault();
        this.close(true);
      } else if (event.key === 'Tab') {
        this.close(true);
      }
    }

    onChange(event) {
      if (!event.target || event.target.type !== 'radio') return;
      this.syncValue();
    }

    onDocumentClick(event) {
      if (this.contains(event.target)) return;
      this.close();
    }
  }

  customElements.define('variant-dropdown', VariantDropdown);
})();
